import { openConfirmDialog } from 'boa-confirm-dialog';
import { createProductCard, buildRegistryScopedCartQuantityByVariantId } from 'boa-registry-product-card';
import { getCart } from '@archetype-themes/scripts/helpers/cart';

/**
 * Shopify Ajax Cart (`/cart.js`) shape used by guest registry list.
 * @typedef {{
 *   items?: Array<{
 *     variant_id?: number|string,
 *     quantity?: number,
 *     properties?: Record<string, unknown>, 
 *   }>,
 * }} ShopifyCart
 */

const ITEMS_PER_PAGE = 10;
/** Max parallel `createProductCard` calls (Storefront + product JSON per line). */
const REGISTRY_CARDS_RENDER_CONCURRENCY = 8;

const registryPath = window.location.pathname;
const isManagementPage = registryPath.includes('baby-registry-management');

const REGISTRY_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const registryPathSegment = registryPath.split('/').pop() || '';
const registryId = REGISTRY_UUID_RE.test(registryPathSegment) ? registryPathSegment : '';
const currentPage = parseInt(localStorage.getItem('currentPage')) || 1;

let isEditingPersonalMessage = false;
let currentRegistryActive = false;
let removeListHandlerAdded = false;

const EMPTY_MESSAGE_ACK_STORAGE_KEY = `boa_baby_registry_empty_message_ack_${registryId}`;

const _locales = JSON.parse(
  document.querySelector('[data-registry-locales]')?.textContent || '{}'
);
const EMPTY_MESSAGE_GREETING = _locales.emptyMessageGreeting || '';
const EMPTY_MESSAGE_BODY = _locales.emptyMessageBody || '';
const EMPTY_MESSAGE_UNDERSTAND = _locales.emptyMessageUnderstand || '';

document.addEventListener('DOMContentLoaded', () => {
  if (isManagementPage) {
    handlePersonalMessage();
  }
});

document.addEventListener('registry:reload', () => {
  window.BoaBabyRegistryBootstrap?.invalidatePrefetch?.();
  delete window.__boaRegistryPrefetch;
  getProductList(registryId, currentPage, ITEMS_PER_PAGE);
});

function initializeRegistry() {
  if (!registryId) {
    console.error('baby registry: missing registry id in URL');
    return;
  }
  getProductList(registryId, currentPage, ITEMS_PER_PAGE);
}

async function fetchRegistryList(id, page, limit = ITEMS_PER_PAGE) {
  if (window.BoaBabyRegistryBootstrap?.fetchRegistry) {
    return window.BoaBabyRegistryBootstrap.fetchRegistry(id, page, limit);
  }

  const response = await fetch(`${REGISTRY_END_POINT}/${id}?page=${page}&limit=${limit}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('error');
  }

  return response.json();
}

/* ── Data Loading ── */

async function getProductList(id, page, limit = ITEMS_PER_PAGE) {
  const registrySection = document.querySelector('.boa-registry');
  registrySection?.removeAttribute('data-registry-ready');
  registrySection?.removeAttribute('data-registry-header-ready');
  document.querySelector('.user-message')?.removeAttribute('data-registry-ready');

  try {
    const cartPromise = isManagementPage
      ? Promise.resolve(null)
      : getCart().catch(() => null);

    const [{
      data,
      active,
      totalItems,
      currentPage: responsePage,
      itemsPerPage,
      personalMessage,
      eventPickerType,
      firstName,
    }, cartSnapshot] = await Promise.all([
      fetchRegistryList(id, page, limit),
      cartPromise,
    ]);

    if (isManagementPage) {
      document.dispatchEvent(
        new CustomEvent('registry:count-changed', {
          detail: { count: totalItems ?? 0, registryId: id },
        }),
      );
    }

    setEventPickerStyles(eventPickerType);
    setPersonalMessage(personalMessage, firstName);

    if (isManagementPage) {
      setWhatsappShareLink();
    }

    registrySection?.setAttribute('data-registry-header-ready', '');
    if (!isManagementPage) {
      document.querySelector('.user-message')?.setAttribute('data-registry-ready', '');
    }

    const isInactivePublicView = !isManagementPage && active === false;

    if (isManagementPage) {
      removeRegistryList(active, data);
    } else if (isInactivePublicView) {
      applyPublicRegistryActiveState(false);
    } else {
      applyPublicRegistryActiveState(true);
      toggleGiftCardBanner(data);
      toggleRegistryContainer(totalItems);
    }

    if (data && Array.isArray(data) && !isInactivePublicView) {
      const cartQuantityByVariantId =
        cartSnapshot != null
          ? buildRegistryScopedCartQuantityByVariantId(cartSnapshot, id)
          : {};

      await renderProductCards(data, cartQuantityByVariantId, cartSnapshot);
      createPagination(totalItems, responsePage, itemsPerPage);
      localStorage.setItem('currentPage', responsePage.toString());
    }

    registrySection?.setAttribute('data-registry-ready', '');
  } catch (error) {
    console.error(error);
    registrySection?.setAttribute('data-registry-header-ready', '');
    registrySection?.setAttribute('data-registry-ready', '');
    document.querySelector('.user-message')?.setAttribute('data-registry-ready', '');
  }
}

if (registryId) {
  initializeRegistry();
}

/* ── Event Picker Styles ── */

function setEventPickerStyles(eventPicker = 'new_born') {
  const container = document.querySelector('.registry-message[data-event-picker]');
  const eventDiv = document.querySelector('.registry-message__event');
  const eventImage = document.querySelector('.registry-message__icon');

  if (!container || !eventDiv) {
    console.error('Missing required elements in event picker', {
      hasContainer: Boolean(container),
      hasEventDiv: Boolean(eventDiv),
    });
    return;
  }

  const eventPickerData = JSON.parse(container.dataset.eventPicker || '[]');
  const eventData = eventPickerData.find((item) => item.type === eventPicker);

  if (!eventData) {
    console.error('Event data not found');
  } else {
    if (eventData.color) {
      eventDiv.style.backgroundColor = eventData.color;
    }

    if (eventImage && eventData.icon) {
      eventImage.src = eventData.icon;
    }

    if (eventImage && eventData.text) {
      eventImage.alt = eventData.text;
    }
  }

  container.classList.remove('registry-message--pending');
}

/* ── Public registry inactive (closed list) ── */

function applyPublicRegistryActiveState(isActive) {
  const registrySection = document.querySelector('.boa-registry');
  if (!registrySection) {
    return;
  }

  const closedMessage = registrySection.querySelector('.boa-registry__list-closed');
  const container = registrySection.querySelector('.registry-container');
  const paginationContainer = registrySection.querySelector('.pagination');

  if (closedMessage) {
    closedMessage.hidden = isActive;
  }

  if (!isActive) {
    registrySection.removeAttribute('disabled');
    if (container) {
      container.innerHTML = '';
    }
    if (paginationContainer) {
      paginationContainer.setAttribute('disabled', '');
      paginationContainer.innerHTML = '';
    }

    const giftCardBanner = document.querySelector('.gift-card-banner');
    const giftCardContent = document.querySelector('.gift-card-banner__content-wrapper');
    const giftCardPopup = document.querySelector('.gift-card-banner__popup-wrapper');
    if (giftCardBanner && giftCardContent && giftCardPopup) {
      giftCardBanner.removeAttribute('disabled');
      giftCardContent.setAttribute('disabled', '');
      giftCardPopup.removeAttribute('disabled');
    }
  }
}

/* ── Registry Container ── */

function toggleRegistryContainer(productsCount) {
  const boaRegistry = document.querySelector('.boa-registry');

  if (boaRegistry) {
    if (productsCount === undefined || productsCount === 0) {
      boaRegistry.setAttribute('disabled', '');
    } else {
      boaRegistry.removeAttribute('disabled');
    }
  }
}

/* ── Gift Card Banner ── */

function toggleGiftCardBanner(data) {
  /**
   * Registry list API `available` can be wrong for grid-bundle parent lines; use remaining quantity
   * (`quantity` vs `purchasedQuantity`) so the banner reflects gifts guests can still buy.
   */
  const filteredProductsCount =
    data?.filter((product) => {
      const requestedQuantity = Number(product.quantity) || 0;
      const purchasedQuantity = Number(product.purchasedQuantity) || 0;
      return requestedQuantity > purchasedQuantity;
    }).length ?? 0;
  const giftCardBanner = document.querySelector('.gift-card-banner');
  const giftCardContent = document.querySelector('.gift-card-banner__content-wrapper');
  const giftCardPopup = document.querySelector('.gift-card-banner__popup-wrapper');

  if (!giftCardBanner || !giftCardContent || !giftCardPopup) {
    console.error('Missing required elements in gift card banner', {
      hasGiftCardBanner: Boolean(giftCardBanner),
      hasGiftCardContent: Boolean(giftCardContent),
      hasGiftCardPopup: Boolean(giftCardPopup),
    });
    return;
  }

  switch (true) {
    case filteredProductsCount === 0:
      giftCardBanner.removeAttribute('disabled');
      giftCardContent.setAttribute('disabled', '');
      giftCardPopup.removeAttribute('disabled');
      break;
    case filteredProductsCount > 0 && filteredProductsCount <= 3:
      giftCardBanner.removeAttribute('disabled');
      giftCardContent.removeAttribute('disabled');
      giftCardPopup.setAttribute('disabled', '');
      break;
    default:
      giftCardBanner.setAttribute('disabled', '');
      giftCardContent.setAttribute('disabled', '');
      giftCardPopup.setAttribute('disabled', '');
      break;
  }
}

/* ── Product Cards ── */

/**
 * @template T, R
 * @param {T[]} items
 * @param {number} batchSize
 * @param {(item: T) => Promise<R>} fn
 * @returns {Promise<R[]>}
 */
async function mapInBatches(items, batchSize, fn) {
  /** @type {R[]} */
  const out = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const part = await Promise.all(batch.map((item) => fn(item)));
    out.push(...part);
  }
  return out;
}

async function renderProductCards(products, cartQuantityByVariantId = {}, cartSnapshot = null) {
  const registrySection = document.querySelector('.boa-registry');

  if (!registrySection) {
    console.error('renderProductCards: .boa-registry not found');
    return;
  }

  const sectionId = registrySection.getAttribute('section-id') || '';
  const container = registrySection.querySelector('.registry-container');

  if (!sectionId || !container) {
    console.error('renderProductCards: missing required elements', {
      hasSectionId: Boolean(sectionId),
      hasContainer: Boolean(container),
    });
    return;
  }

  // Guests: render every registry line. Do not pre-filter by API `available` (incorrect for some grid bundles);
  // each card refreshes stock via Storefront / product JSON in createProductCard.

  const cardElements = await mapInBatches(products, REGISTRY_CARDS_RENDER_CONCURRENCY, (product) =>
    createProductCard(product, {
      registryId,
      isManagementPage,
      cartQuantityByVariantId,
      sectionId,
      cart: cartSnapshot,
    }),
  );

  const fragment = document.createDocumentFragment();
  for (const card of cardElements) {
    if (card) {
      fragment.appendChild(card);
    }
  }

  container.replaceChildren(...fragment.childNodes);
}

/* ── Pagination ── */

function createPagination(totalItems, page, itemsPerPage) {
  const paginationContainer = document.querySelector('.boa-registry .pagination');
  if (!paginationContainer) {
    console.error('Missing required elements in pagination', {
      hasPaginationContainer: Boolean(paginationContainer),
    });
    return;
  }

  if (totalItems < ITEMS_PER_PAGE) {
    paginationContainer.setAttribute('disabled', '');
  } else {
    paginationContainer.removeAttribute('disabled');
  }

  paginationContainer.innerHTML = '';
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  for (let pageIndex = 1; pageIndex <= totalPages; pageIndex++) {
    const pageLink = document.createElement('a');
    pageLink.href = '#';
    pageLink.textContent = pageIndex;
    pageLink.className = pageIndex === page ? 'active' : '';
    pageLink.onclick = (event) => {
      event.preventDefault();
      window.BoaBabyRegistryBootstrap?.invalidatePrefetch?.();
      delete window.__boaRegistryPrefetch;
      getProductList(registryId, pageIndex, itemsPerPage);
      const targetElement = document.querySelector('.boa-registry');
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    paginationContainer.appendChild(pageLink);
  }
}

/* ── Remove Registry List ── */

function removeRegistryList(active, data) {
  const removeListButton = document.getElementById('remove-list-button');

  if (!removeListButton) {
    console.error('Missing required elements in remove list button', {
      hasRemoveListButton: Boolean(removeListButton),
    });
    return;
  }

  currentRegistryActive = active;

  if (!removeListHandlerAdded) {
    removeListHandlerAdded = true;
    removeListButton.addEventListener('click', () => {
      if (currentRegistryActive) {
        const message = document.getElementById('ConfirmDialog').dataset.removeList;
        openConfirmDialog(message, () => {
          updateActivatedRegistryList(false);
        });
      } else {
        updateActivatedRegistryList(true);
      }
    });
  }
}

async function updateActivatedRegistryList(isActive) {
  const errorElement = document.querySelector('.baby-registry-error-banner.hidden');
  const errorClass = '.baby-registry-error-banner';
  if (!errorElement) {
    hiddenError(errorClass);
  }

  try {
    const response = await fetch(`${REGISTRY_END_POINT}/${registryId}/activate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: isActive }),
    });

    if (!response.ok) {
      throw new Error('somethingWrong');
    }

    const { success, active } = await response.json();
    if (!success) {
      showError(errorClass, 'alreadyActiveList');
      return;
    }

    if (!active) {
      sessionStorage.removeItem(REGISTRY_ID_STORAGE_KEY);
      document.dispatchEvent(
        new CustomEvent('registry:count-changed', { detail: { count: 0 } }),
      );
      window.location.href = '/pages/baby-registry-form';
    }
  } catch (error) {
    console.error(error);
  }
}

/* ── Personal Message ── */

function maybeSavePersonalMessage(elements) {
  const trimmed = elements.messageTextarea.value.trim();

  if (trimmed || sessionStorage.getItem(EMPTY_MESSAGE_ACK_STORAGE_KEY) === '1') {
    handlePersonalMessageUpdate(elements);
    return;
  }

  openConfirmDialog('', () => {
    sessionStorage.setItem(EMPTY_MESSAGE_ACK_STORAGE_KEY, '1');
    handlePersonalMessageUpdate(elements);
  }, emptyPersonalMessageDialogOptions());
}

function emptyPersonalMessageDialogOptions() {
  return {
    confirmLabel: EMPTY_MESSAGE_UNDERSTAND,
    decorateWithHeart: true,
    greeting: EMPTY_MESSAGE_GREETING,
    body: EMPTY_MESSAGE_BODY,
  };
}

/** @returns {boolean} true if navigation should proceed (caller must not preventDefault) */
function maybeOpenWhatsappShare(elements) {
  const trimmed = elements.messageTextarea.value.trim();

  if (trimmed || sessionStorage.getItem(EMPTY_MESSAGE_ACK_STORAGE_KEY) === '1') {
    setWhatsappShareLink();
    return true;
  }

  openConfirmDialog('', () => {
    sessionStorage.setItem(EMPTY_MESSAGE_ACK_STORAGE_KEY, '1');
    setWhatsappShareLink();
    window.open(elements.whatsappShareLink.href, '_blank', 'noopener,noreferrer');
  }, emptyPersonalMessageDialogOptions());

  return false;
}

function getPersonalMessageElements() {
  const required = {
    messageTextarea: document.querySelector('#registryTextarea'),
    saveBtn: document.querySelector('#message-button-save'),
    toggleEditBtn: document.querySelector('#message-button-edit-toggle'),
    divider: document.querySelector('.registry-message-buttons__divider'),
    whatsappShareLink: document.getElementById('whats-app-share-link'),
  };

  const missing = Object.entries(required)
    .filter(([, el]) => !el)
    .map(([key]) => key);

  if (missing.length) {
    console.error('Missing required elements in personal message', { missing });
    return null;
  }

  return required;
}

function setPersonalMessage(message, firstName) {
  const currentMessage = message ?? '';
  const currentFirstName = firstName ?? '';

  if (isManagementPage) {
    const elements = getPersonalMessageElements();

    if (!elements) {
      return;
    }

    const { messageTextarea, toggleEditBtn, divider } = elements;
    messageTextarea.value = currentMessage;
    toggleEditBtn.hidden = !currentMessage.trim();
    divider.hidden = !currentMessage.trim();
    messageTextarea.classList.toggle('registry-message__textarea_disabled', currentMessage.trim().length > 0);
    messageTextarea.setAttribute('tabindex', currentMessage.trim().length > 0 ? '-1' : '0');
  } else {
    const messageHeader = document.querySelector('.user-message__heading');
    if (messageHeader) {
      messageHeader.textContent = `רשימת המתנות של ${currentFirstName.trim()}`;
    }

    const messageParagraph = document.querySelector('.user-message__subtext');
    if (messageParagraph) {
      messageParagraph.textContent = currentMessage;
    }
  }
}

function handlePersonalMessage() {
  const elements = getPersonalMessageElements();

  if (!elements) {
    return;
  }

  const { saveBtn, toggleEditBtn, messageTextarea, whatsappShareLink, divider } = elements;

  saveBtn.addEventListener('click', () => maybeSavePersonalMessage(elements));

  whatsappShareLink.addEventListener('click', (event) => {
    if (!maybeOpenWhatsappShare(elements)) {
      event.preventDefault();
    }
  });

  toggleEditBtn.addEventListener('click', () => {
    isEditingPersonalMessage = !isEditingPersonalMessage;

    if (isEditingPersonalMessage) {
      messageTextarea.classList.remove('registry-message__textarea_disabled');
      whatsappShareLink.classList.add('baby-registry-btn_disabled');
    } else {
      messageTextarea.classList.add('registry-message__textarea_disabled');
      whatsappShareLink.classList.remove('baby-registry-btn_disabled');
    }
  });

  messageTextarea.addEventListener('focus', () => {
    whatsappShareLink.classList.add('baby-registry-btn_disabled');
  });

  messageTextarea.addEventListener('blur', () => {
    whatsappShareLink.classList.remove('baby-registry-btn_disabled');
  });

  const isEmpty = !messageTextarea.value.trim();
  toggleEditBtn.hidden = isEmpty;
  divider.hidden = isEmpty;
  messageTextarea.classList.toggle('registry-message__textarea_disabled', !isEmpty);
  messageTextarea.setAttribute('tabindex', isEmpty ? '0' : '-1');
}

async function handlePersonalMessageUpdate(elements) {
  const { messageTextarea, whatsappShareLink, saveBtn, toggleEditBtn, divider } = elements;

  const message = messageTextarea.value.trim();

  saveBtn.disabled = true;
  toggleEditBtn.disabled = true;
  whatsappShareLink.classList.add('baby-registry-btn_disabled');
  messageTextarea.setAttribute('tabindex', '-1');

  try {
    const response = await fetch(`${REGISTRY_END_POINT}/${registryId}/message`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ personalMessage: message }),
    });

    if (!response.ok) {
      showError('.baby-registry-error-banner', 'somethingWrong');
      saveBtn.disabled = false;
      toggleEditBtn.disabled = false;
      whatsappShareLink.classList.remove('baby-registry-btn_disabled');
      messageTextarea.setAttribute('tabindex', '0');
      return;
    }

    const { success } = await response.json();

    if (success) {
      const isEmpty = !message.length;

      toggleEditBtn.hidden = isEmpty;
      divider.hidden = isEmpty;
      messageTextarea.classList.toggle('registry-message__textarea_disabled', !isEmpty);
      messageTextarea.setAttribute('tabindex', isEmpty ? '0' : '-1');
      setWhatsappShareLink();
      updateButtonOnSuccess({ saveBtn, toggleEditBtn, whatsappShareLink });
    } else {
      saveBtn.disabled = false;
      toggleEditBtn.disabled = false;
      whatsappShareLink.classList.remove('baby-registry-btn_disabled');
    }
  } catch (error) {
    console.error(error);
    saveBtn.disabled = false;
    toggleEditBtn.disabled = false;
    whatsappShareLink.classList.remove('baby-registry-btn_disabled');
  }
}

function updateButtonOnSuccess({ saveBtn, toggleEditBtn, whatsappShareLink }) {
  isEditingPersonalMessage = false;
  saveBtn.textContent = _locales.messageSaved || '';

  setTimeout(() => {
    saveBtn.textContent = _locales.saveMessage || '';
    saveBtn.disabled = false;
    toggleEditBtn.disabled = false;
    whatsappShareLink.classList.remove('baby-registry-btn_disabled');
  }, 2000);
}

/* ── WhatsApp Share ── */

function setWhatsappShareLink() {
  const whatsappShareLink = document.getElementById('whats-app-share-link');

  if (!whatsappShareLink) {
    console.error('Whatsapp share link not found', {
      hasWhatsappShareLink: Boolean(whatsappShareLink),
    });
    return;
  }

  const origin = window.location.origin;
  const registryUrl = `${origin}/pages/baby-registry/${registryId}`;
  const message = document.querySelector('#registryTextarea')?.value?.trim() ?? '';
  const text = message ? `${message}\n\n${registryUrl}` : registryUrl;
  whatsappShareLink.href = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
  whatsappShareLink.target = '_blank';
  whatsappShareLink.rel = 'noopener';
}
